import type { Backlink } from '@api/types/backlinks';
import routes from '@constants/routes';
import {
  ArrowLeftRight,
  Calendar,
  FileText,
  Flag,
  SquareActivity,
  Table2,
  Tag,
} from 'lucide-react';
import type { ReactNode } from 'react';
import type { RelatedLinkItem } from '@shared/ui';

const sourceIcon = (sourceType: Backlink['sourceType']): ReactNode => {
  switch (sourceType) {
    case 'document':
      return <FileText size={16} />;
    case 'meeting':
      return <Calendar size={16} />;
    case 'status-task':
      return <SquareActivity size={16} />;
    // The data-analysis sources (0.54.0): a working paper, a tickmark's meaning, a
    // Finding's reason. Each gets a real icon rather than the request fallback.
    case 'analysis-view':
      return <Table2 size={16} />;
    case 'analysis-symbol':
      return <Tag size={16} />;
    case 'analysis-finding':
      return <Flag size={16} />;
    case 'request':
    case 'quote-request':
    case 'request-container':
    default:
      return <ArrowLeftRight size={16} />;
  }
};

export interface BacklinkSourceLabels {
  request: string;
  task: string;
  meeting: string;
  document: string;
  /** Optional so existing callers keep compiling; the raw type is the fallback. */
  view?: string;
  tickmark?: string;
  finding?: string;
  /** Why a Finding's row cannot be opened yet (see `backlinkHref`). Required, so no
   *  caller can list a Finding without saying why it does not open. */
  findingUnavailable: string;
}

const sourceLabel = (backlink: Backlink, labels: BacklinkSourceLabels) => {
  switch (backlink.sourceType) {
    case 'document':
      return labels.document;
    case 'meeting':
      return labels.meeting;
    case 'status-task':
      return labels.task;
    case 'analysis-view':
      return labels.view ?? backlink.sourceType;
    case 'analysis-symbol':
      return labels.tickmark ?? backlink.sourceType;
    case 'analysis-finding':
      return labels.finding ?? backlink.sourceType;
    case 'request':
    case 'quote-request':
    case 'request-container':
    default:
      return labels.request;
  }
};

const statusStepHref = (auditId: string, backlink: Backlink) => {
  const openedStep = backlink.parentTaskId || backlink.sourceId;
  const activeGroup = backlink.groupId ?? '';
  const base = `${routes.dashboard.status(auditId)}?activeGroup=${activeGroup}&openedStep=${openedStep}`;
  return backlink.parentTaskId ? `${base}&openedSubtask=${backlink.sourceId}` : base;
};

export const backlinkHref = (auditId: string | undefined, backlink: Backlink) => {
  if (!auditId) return undefined;
  switch (backlink.sourceType) {
    case 'document':
      return routes.dashboard.document(auditId, backlink.sourceId);
    case 'meeting':
      return routes.dashboard.meeting(auditId, backlink.sourceId);
    case 'status-task':
      return statusStepHref(auditId, backlink);
    case 'analysis-view':
      return routes.dashboard.dataAnalysisView(auditId, backlink.sourceId);
    // No page opens a Finding from its address yet: nothing reads `?finding=`
    // (`routes.dashboard.dataAnalysisFinding`) until the data page hosts one (#636).
    // The row is disabled with a hint instead of landing on a page with nothing open.
    case 'analysis-finding':
      return undefined;
    case 'analysis-symbol':
      // A tickmark has no address of its own; the workspace is where its meaning lives.
      return routes.dashboard.dataAnalysis(auditId);
    case 'request':
    case 'quote-request':
    case 'request-container':
    default:
      return routes.dashboard.request(auditId, backlink.sourceId);
  }
};

export function buildDocumentRelatedLinkItems(
  auditId: string | undefined,
  backlinks: readonly Backlink[] = [],
  labels: BacklinkSourceLabels,
): RelatedLinkItem[] {
  return backlinks.map((backlink) => ({
    id: backlink.id,
    href: backlinkHref(auditId, backlink),
    title: backlink.sourceTitle,
    subtitle: sourceLabel(backlink, labels),
    icon: sourceIcon(backlink.sourceType),
    ...(backlink.sourceType === 'analysis-finding'
      ? { disabledHint: labels.findingUnavailable }
      : {}),
  }));
}
